import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  UploadStatus,
  useVehicleGalleryContext,
} from '@/contexts/vehicle-gallery-provider';
import { toast } from '@/hooks/use-toast';
import { pdkAxios } from '@/lib/pdk-axios';
import {
  VehicleImageCategoryEnum,
  type VehicleImageCategory,
} from '@/models/types';
import { useMutation } from '@tanstack/react-query';
import { BookOpenCheck, Check, CircleX, Group } from 'lucide-react';
import { useState } from 'react';

type AssignImageCategoriesPayload = {
  image_id: number;
  category: string;
}[];

type AssignCategoryMutateParams = {
  payload: AssignImageCategoriesPayload;
};

/**
 * Responsible for:
 *  - Bluk image category assginment
 *  - Default category assignment
 */
export const AssignImageCategoryBtn = () => {
  const {
    vehicleImages,
    showImageCategoryEditor,
    clearSelectedImageIDs,
    toggleShowCategoryEditor,
    uploadStatus,
    selectedImageIDs,
    availableImageCategories,
    forceReload,
  } = useVehicleGalleryContext();
  const [selectedCategory, setSelectedCategory] =
    useState<VehicleImageCategory | null>(null);
  const [isAutoAssignModalOpen, setIsAutoAssignModalOpen] = useState(false);

  const { mutate: assignCategories, isPending: isUpdatingCategory } =
    useMutation({
      mutationFn: async ({ payload }: AssignCategoryMutateParams) => {
        try {
          await pdkAxios.post('/inventory/image-category/assign', payload);
          forceReload();
        } catch (err) {
          throw err;
        }
      },
      onSuccess: () => {
        toggleShowCategoryEditor();
        clearSelectedImageIDs();
        setSelectedCategory(null);
      },
      onError: (err) => {
        toast({
          title: 'Error updating category',
          description: err.message || 'Unexpected error ocurred',
        });
      },
    });

  const autoAssignCategoriesCb = () => {
    if (vehicleImages.length < 8) {
      toast({
        variant: 'destructive',
        title: 'Invalid action',
        description:
          'Since auto-category assignment works based on image order index there needs to be at least 8 images uploaded first.',
      });
      return;
    }

    const payload: AssignImageCategoriesPayload = [
      {
        image_id: +vehicleImages[2].id,
        category: VehicleImageCategoryEnum.FrontSeats,
      },
      {
        image_id: +vehicleImages[3].id,
        category: VehicleImageCategoryEnum.RearSeats,
      },
      {
        image_id: +vehicleImages[4].id,
        category: VehicleImageCategoryEnum.Infotainment,
      },
      {
        image_id: +vehicleImages[5].id,
        category: VehicleImageCategoryEnum.CargoSpace,
      },
      {
        image_id: +vehicleImages[6].id,
        category: VehicleImageCategoryEnum.Wheels,
      },
      {
        image_id: +vehicleImages[7].id,
        category: VehicleImageCategoryEnum.PlugType,
      },
    ];

    assignCategories({ payload });
  };

  const cancelCb = () => {
    toggleShowCategoryEditor();
    clearSelectedImageIDs();
    setSelectedCategory(null);
  };

  if (uploadStatus !== UploadStatus.NONE) {
    return null;
  }

  if (!showImageCategoryEditor) {
    return (
      <div className="img-category-actions flex flex-row gap-2">
        <Button
          className="cursor-pointer flex gap-2"
          variant="outline"
          onClick={toggleShowCategoryEditor}
        >
          <Group />
          <span>Manage Categories</span>
        </Button>
      </div>
    );
  }

  return (
    <div className="img-category-actions flex flex-row gap-2">
      {/* We don't want to show this button if user selected images in order to avoid user accidently clicking on it */}
      {selectedImageIDs.length === 0 && (
        <Button
          variant="default"
          className="bg-blue-500 cursor-pointer hover:bg-blue-700"
          onClick={() => setIsAutoAssignModalOpen(true)}
          disabled={isUpdatingCategory}
        >
          <BookOpenCheck />
          <span>Auto assign</span>
        </Button>
      )}

      <DropdownMenu>
        <DropdownMenuTrigger asChild disabled={isUpdatingCategory}>
          <div className="px-3 pt-2 border-1 border-black rounded-md cursor-pointer">
            <span>Selected category: </span>
            <span className="font-bold">
              {selectedCategory == null ? 'None' : selectedCategory.label}
            </span>
          </div>
        </DropdownMenuTrigger>

        <DropdownMenuContent
          className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
          align="start"
          side="bottom"
          sideOffset={4}
        >
          <DropdownMenuLabel className="text-xs text-muted-foreground">
            Categories
          </DropdownMenuLabel>
          {availableImageCategories.map((category) => (
            <DropdownMenuItem
              key={category.value}
              onClick={() => setSelectedCategory(category)}
            >
              {category.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      {selectedCategory != null && (
        <Button
          className="cursor-pointer"
          variant="default"
          onClick={() =>
            assignCategories({
              payload: selectedImageIDs.map((imgID) => ({
                image_id: +imgID,
                category: selectedCategory.value,
              })),
            })
          }
          disabled={isUpdatingCategory}
        >
          <Check />
          <span>Assign</span>
        </Button>
      )}

      <Button
        className="cursor-pointer flex gap-2 border-red-600 text-red-600 hover:text-red-600 hover:bg-red-100"
        variant="outline"
        onClick={cancelCb}
        disabled={isUpdatingCategory}
      >
        <CircleX />
        <span>Cancel</span>
      </Button>

      <Dialog open={isAutoAssignModalOpen} modal>
        <DialogContent onClickClose={() => setIsAutoAssignModalOpen(false)}>
          <DialogTitle className="font-bold">
            Auto-category assignment confirmation
          </DialogTitle>

          <p>Are you sure you want to perform auto-category assigment?</p>
          <p>
            Please note that auto-category assignment performs assigment based
            on image order index:
          </p>
          <ol>
            <li>Image 3 - Front seats</li>
            <li>Image 4 - Rear seats</li>
            <li>Image 5 - Infotainment</li>
            <li>Image 6 - Cargo space</li>
            <li>Image 7 - Wheels</li>
            <li>Image 8 - Plug type</li>
          </ol>

          <DialogFooter>
            <Button
              className="cursor-pointer flex gap-2 border-red-600 text-red-600 hover:text-red-600 hover:bg-red-100"
              variant="outline"
              onClick={() => setIsAutoAssignModalOpen(false)}
            >
              Close
            </Button>
            <Button
              variant="default"
              className="bg-blue-700 cursor-pointer hover:bg-blue-800"
              onClick={autoAssignCategoriesCb}
            >
              Auto-assign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
